import { API_PUBLIC_SHARE_URL } from './index';
import { formatCount } from './counts';

/**
 * Files on public links (PR 10c, the 10c contract; owner rulings F10,
 * G12–G16): what a public page needs to show a file, and nothing that needs a
 * session.
 *
 * A public item carries `files[id] = { name, ext, size, image, variants,
 * download: 'original' | 'signin' | 'checking' | 'unavailable', thumb }` for
 * its file elements' values,
 * and an item link's item its Other attachments in the same shape (+ `id`).
 * The grid draws the inline `thumb` (the payload's `sm` as a data URI — no
 * byte request per tile, so a page of photos never trips the WAF's
 * anonymous budget); the item page asks for `md`, full screen for `lg`, and
 * Download for the file itself, each by a plain URL — an <img> or an <a>,
 * which carries no Authorization header, so the bytes are fetched as a
 * visitor whoever is signed in on the machine.
 */

const filesBase = code => `${API_PUBLIC_SHARE_URL}/${encodeURIComponent(code)}/files`;

/** A server-made size of a public photo (`sm` | `md` | `lg`), as `image/webp`. */
export const publicFileVariantUrl = (code, fileId, size) =>
	`${filesBase(code)}/${encodeURIComponent(fileId)}?size=${encodeURIComponent(size)}`;

/** The download: a photo without its location, or a safe document as it is (G14, G15). */
export const publicFileDownloadUrl = (code, fileId) =>
	`${filesBase(code)}/${encodeURIComponent(fileId)}/download`;

/** "Sign in to download": the sign-in page, and back to this public page after. */
export const signInToDownloadPath = code =>
	`/login?isRedirect=true&returnPath=${encodeURIComponent(`/s/${code}`)}`;

/** May a visitor download it? Only what the API marked `original` (G15's safe types). */
export const visitorMayDownload = file => file?.download === 'original';

/**
 * The malware gate (PR 10e): a public download is served only for a file the
 * scan called clean. A verdict still on its way is `checking`; a flagged
 * file is `unavailable`; a file the scan never read is `signin` (10c's). The
 * values a public page knows, and its words for the two that are not a door:
 */
export const PUBLIC_DOWNLOAD_VALUES = ['original', 'signin', 'checking', 'unavailable'];

export const PUBLIC_DOWNLOAD_NOTES = {
	checking: 'Checking this file. Try again in a minute.',
	unavailable: 'This file isn\'t available.',
};

/** The line a visitor reads in place of Download, or null (Download, or Sign in). */
export const publicDownloadNote = file => PUBLIC_DOWNLOAD_NOTES[file?.download] || null;

// Only a WebP data URI is drawn: the payload's inline `sm`, and nothing a
// payload could smuggle in its place (another type, a URL).
const THUMB = /^data:image\/webp;base64,[A-Za-z0-9+/]+={0,2}$/;
export const safeThumb = value => (typeof value === 'string' && THUMB.test(value) ? value : null);

/**
 * The create dialog's words for the preview's count (the 10c contract):
 * "Includes N files on these items." — or, with none, a sentence and no zero.
 */
export function includesFilesText(count) {
	if (!count) return null;
	const files = formatCount(count.total, 'file');
	// A preview past the API's item cap counted the newest items only.
	if (count.capped) return files ? `Includes at least ${files} on these items.` : null;
	return files ? `Includes ${files} on these items.` : 'No files on these items right now.';
}

/** The G15 note, when some of those files are not downloadable by visitors. */
export function restrictedFilesText(count) {
	if (!count || !(count.restricted > 0)) return null;
	return `${count.restricted} of them can't be downloaded by visitors (archives, macro documents and other types); `
		+ 'they show by name with Sign in to download.';
}

/**
 * A manager's line for an attachment's public downloads — the attachments
 * list's `publicDownloads: [{ link_id, name, count }]`, the ledger's
 * `via: 'public'` rows counted per link, sent to the collection's managers
 * only: "Downloaded 3 times through the public link Molly's family". A link
 * since reaped (or never named) has `name: null`: "through a public link".
 */
export function publicDownloadLines(publicDownloads) {
	if (!Array.isArray(publicDownloads)) return [];
	return publicDownloads
		.filter(entry => Number.isInteger(entry?.count) && entry.count > 0)
		.map((entry) => {
			const times = entry.count === 1 ? 'once' : `${entry.count} times`;
			const raw = entry.name ?? entry.linkName;
			const name = typeof raw === 'string' && raw.trim() ? raw.trim() : '';
			return name ? `Downloaded ${times} through the public link ${name}` : `Downloaded ${times} through a public link`;
		});
}
